// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Anchor,
  Button,
  Checkbox,
  Divider,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import {
  IconBrandApple,
  IconBrandFacebook,
  IconBrandGoogle,
  IconLock,
  IconMail,
  IconUser,
} from '@tabler/icons-react';
import { useState, useCallback } from 'react';
import type { JSX } from 'react';
import { Link, useNavigate } from 'react-router';
import { AuthLayout } from '../components/auth/AuthLayout';
import classes from './RegisterPage.module.css';

interface RegisterFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

export function RegisterPage(): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);

  const form = useForm<RegisterFormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      agreeToTerms: false,
    },
    validate: {
      firstName: (value) => (!value ? 'First name is required' : null),
      lastName: (value) => (!value ? 'Last name is required' : null),
      email: (value) => {
        if (!value) return 'Email is required';
        if (!/^\S+@\S+$/.test(value)) return 'Invalid email address';
        return null;
      },
      password: (value) => {
        if (!value) return 'Password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        return null;
      },
      confirmPassword: (value, values) => {
        if (!value) return 'Please confirm your password';
        if (value !== values.password) return 'Passwords do not match';
        return null;
      },
      agreeToTerms: (value) => (!value ? 'You must agree to the terms and conditions' : null),
    },
  });

  const handleSubmit = useCallback(
    async (values: RegisterFormValues): Promise<void> => {
      setLoading(true);
      try {
        // Use the auth/newuser endpoint for registration
        await medplum.post('auth/newuser', {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
          remember: true,
        });
        showNotification({
          color: 'green',
          title: 'Registration successful',
          message: 'Please check your email to verify your account.',
        });
        navigate('/signin');
      } catch (err) {
        showNotification({
          color: 'red',
          title: 'Registration failed',
          message: normalizeErrorString(err),
          autoClose: false,
        });
      } finally {
        setLoading(false);
      }
    },
    [medplum, navigate]
  );

  const handleSocialLogin = useCallback((provider: string) => {
    showNotification({
      color: 'blue',
      title: 'Coming Soon',
      message: `${provider} registration is not yet available.`,
    });
  }, []);

  return (
    <AuthLayout title="Create Account" subtitle="Fill in the details below to create your account">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Group grow>
            <TextInput
              label="First Name"
              placeholder="Enter first name"
              leftSection={<IconUser size={16} />}
              size="md"
              {...form.getInputProps('firstName')}
            />
            <TextInput
              label="Last Name"
              placeholder="Enter last name"
              leftSection={<IconUser size={16} />}
              size="md"
              {...form.getInputProps('lastName')}
            />
          </Group>

          <TextInput
            label="Email Address"
            placeholder="Enter Email Address"
            leftSection={<IconMail size={16} />}
            size="md"
            {...form.getInputProps('email')}
          />

          <PasswordInput
            label="Password"
            placeholder="Enter Password"
            leftSection={<IconLock size={16} />}
            size="md"
            {...form.getInputProps('password')}
          />

          <PasswordInput
            label="Confirm Password"
            placeholder="Confirm your password"
            leftSection={<IconLock size={16} />}
            size="md"
            {...form.getInputProps('confirmPassword')}
          />

          <Checkbox
            label={
              <Text size="sm">
                I agree to the{' '}
                <Anchor href="/terms" target="_blank" className={classes.termsLink}>
                  Terms & Conditions
                </Anchor>{' '}
                and{' '}
                <Anchor href="/privacy" target="_blank" className={classes.termsLink}>
                  Privacy Policy
                </Anchor>
              </Text>
            }
            size="sm"
            {...form.getInputProps('agreeToTerms', { type: 'checkbox' })}
          />

          <Button
            type="submit"
            fullWidth
            size="md"
            loading={loading}
            className={classes.submitButton}
          >
            Create Account
          </Button>

          <Divider label="OR" labelPosition="center" />

          <Group grow gap="sm">
            <ActionIcon
              variant="outline"
              size="xl"
              radius="md"
              className={classes.socialButton}
              aria-label="Sign up with Facebook"
              onClick={() => handleSocialLogin('Facebook')}
            >
              <IconBrandFacebook size={20} color="#1877F2" />
            </ActionIcon>
            <ActionIcon
              variant="outline"
              size="xl"
              radius="md"
              className={classes.socialButton}
              onClick={() => handleSocialLogin('Google')}
              aria-label="Sign up with Google"
            >
              <IconBrandGoogle size={20} color="#DB4437" />
            </ActionIcon>
            <ActionIcon
              variant="outline"
              size="xl"
              radius="md"
              className={classes.socialButton}
              aria-label="Sign up with Apple"
              onClick={() => handleSocialLogin('Apple')}
            >
              <IconBrandApple size={20} />
            </ActionIcon>
          </Group>

          <Text ta="center" size="sm" c="dimmed">
            Already have an account?{' '}
            <Anchor component={Link} to="/signin" className={classes.signInLink}>
              Sign In
            </Anchor>
          </Text>
        </Stack>
      </form>
    </AuthLayout>
  );
}
